import { isAuthoritativeProjection } from '../composer/composerUiProjectionSelector.js';

const PROMPT_TEMPLATE_COLLAPSED_COUNT = 3;
const STARTER_ENTRY_SURFACES = new Set(['conversation_draft']);

const trimText = (value = '') => String(value || '').trim();
const asArray = (value) => (Array.isArray(value) ? value : []);
const PANEL_TITLES = new Map([
  ['contextual_next_action', ({ recommendationPanel }) => trimText(recommendationPanel.title) || '推荐下一步'],
  ['starter', ({ resolvedUILabels }) => trimText(resolvedUILabels.scenario_panel_title) || '起步入口'],
  ['hidden', () => ''],
]);

/** Presentation kind is resolved here; renderers must not turn a missing next-action title into a starter. */
export function resolveRecommendationTitle({ recommendationPanel, starterScenariosSuppressed, resolvedUILabels = {} }) {
  const presentations = [
    { matches: isAuthoritativeProjection(recommendationPanel) && recommendationPanel.visible === true,
      kind: 'contextual_next_action' },
    { matches: !starterScenariosSuppressed, kind: 'starter' },
    { matches: true, kind: 'hidden' },
  ];
  const { kind } = presentations.find(({ matches }) => matches);
  return PANEL_TITLES.get(kind)({ recommendationPanel, resolvedUILabels });
}

/** Only an application-owned draft can offer starter content; missing recommendations are not admission. */
export function resolveStarterSuppressed({
  workspaceSurface,
  workspaceSuspension,
  activeComposerChooser,
  composerLoading,
  showStarterScenarios,
  recommendationPanel,
}) {
  return [
    !STARTER_ENTRY_SURFACES.has(workspaceSurface),
    workspaceSuspension?.active === true,
    Boolean(workspaceSuspension?.error),
    isAuthoritativeProjection(recommendationPanel),
    Boolean(activeComposerChooser),
    showStarterScenarios === false,
    composerLoading === true,
  ].some(Boolean);
}

export function hasActiveQuestionChooser(composerChooser = null) {
  if (!composerChooser) {
    return false;
  }
  const pages = asArray(composerChooser.pages);
  return Boolean(composerChooser.currentQuestion)
    || trimText(composerChooser.kind) === 'intake_batch'
    || pages.some((page) => page?.currentQuestion);
}

export function normalizeStarterScenarios(starterScenarios = []) {
  return asArray(starterScenarios).filter((entry) => (
    trimText(entry?.starter_group) !== 'manual_workflow_entry'
    && trimText(entry?.source) !== 'manual_workflow_entry'
  ));
}

export function selectVisibleStarterScenarios({
  normalizedStarterScenarios = [],
  showAllScenarios = false,
  scenarioRandomSeed = 0,
} = {}) {
  const scenarios = asArray(normalizedStarterScenarios);
  return showAllScenarios ? scenarios : rotateScenarios(scenarios, scenarioRandomSeed).slice(0, 2);
}

export function selectVisiblePromptTemplateCards({
  promptTemplateCards = [],
  showAllScenarios = false,
} = {}) {
  const cards = asArray(promptTemplateCards);
  return showAllScenarios ? cards : cards.slice(0, PROMPT_TEMPLATE_COLLAPSED_COUNT);
}

export function selectVisibleScenarios({
  hasPromptTemplateCards,
  normalizedStarterScenarios,
  promptPanelSuppressed,
  promptTemplateCards,
  scenarioRandomSeed,
  showAllScenarios,
  starterScenariosSuppressed,
} = {}) {
  if (promptPanelSuppressed || starterScenariosSuppressed && !hasPromptTemplateCards) {
    return [];
  }
  return hasPromptTemplateCards
    ? selectVisiblePromptTemplateCards({ promptTemplateCards, showAllScenarios })
    : selectVisibleStarterScenarios({
        normalizedStarterScenarios,
        showAllScenarios,
        scenarioRandomSeed,
      });
}

export function canExpandScenarioCards({
  hasPromptTemplateCards,
  normalizedStarterScenarios,
  promptPanelSuppressed,
  promptTemplateCards,
  starterScenariosSuppressed,
} = {}) {
  if (promptPanelSuppressed || starterScenariosSuppressed && !hasPromptTemplateCards) {
    return false;
  }
  return hasPromptTemplateCards
    ? promptTemplateCards.length > PROMPT_TEMPLATE_COLLAPSED_COUNT
    : normalizedStarterScenarios.length > 2;
}

export function resolvePanelDismissed({
  hasPromptTemplateCards,
  promptPanelSuppressed,
  scenarioDismissed,
  scenarioRecommendationConsumed,
  starterScenariosSuppressed,
} = {}) {
  if (promptPanelSuppressed || starterScenariosSuppressed && !hasPromptTemplateCards) {
    return true;
  }
  return !hasPromptTemplateCards && (scenarioRecommendationConsumed || scenarioDismissed);
}

function rotateScenarios(starterScenarios = [], seed = 0) {
  const scenarios = asArray(starterScenarios);
  if (scenarios.length < 2) {
    return scenarios;
  }
  const numericSeed = Number(seed);
  const boundedSeed = Number.isFinite(numericSeed) ? Math.abs(numericSeed % 1) : 0;
  const offset = Math.min(scenarios.length - 1, Math.floor(boundedSeed * scenarios.length));
  return [...scenarios.slice(offset), ...scenarios.slice(0, offset)];
}
